import { CommonApiResponse } from 'src/app/shared/interface/common.interface';
import { ValuationType } from '../../order/interface/order.interface';
import { ExtendedPropertyDetail } from '../../property/interface/property.interface';

export interface ValidationResult {
  scope: ValidationScope;
  propertyId?: string;
  path: Array<string>;
  fieldName: string;
  page: Array<string>;
  status: RuleStatus | null;
  description: string;
  label?: string;
  valuationType?: ValuationType;
  scenarioId?: string | null;
  structureIndex?: number;
  unitIndex?: number;
  source?: string | null;
  ruleId?: string | null;
  comparableNumber?: number | null;
  allowOverride?: boolean;
  isOverridden?: boolean;
  userNotes?: string | null;
  frontendRule?: boolean;
  validationType?: ValidationType;
}

/** A single still-failing per-field rule produced by the live evaluator. */
export interface FieldRuleFailure {
  rule: ValidationRule;
  message: string;
  status: RuleStatus;
}

export interface CreateValidationItemParam {
  rule: ValidationRule;
  status: RuleStatus;
  scope: ValidationScope;
  page: string[];
  propertyId?: string;
  address?: string;
  valuationType?: ValuationType;
  scenarioId?: string | null;
  description?: string | null;
  structureIndex?: number;
  unitIndex?: number;
}

export interface ValidationList {
  section: string;
  list: ValidationResult[];
}

export type RuleCount = {
  [key in RuleStatusKey]: number;
};

export enum RuleStatus {
  hardstop = 'HARDSTOP',
  error = 'ERROR',
  warning = 'WARNING',
  success = 'SUCCESS',
  system_error = 'SYSTEM_ERROR',
}

export type RuleStatusKey = 'hardstop' | 'error' | 'warning' | 'total' | 'section' | 'system_error';

export interface ValidationRuleResponse {
  reportType: string;
  validationRules: Array<Rule>;
}

export interface ReviewValidationResponse {
  ruleDetails: Array<ExternalValidationResult>;
}

export interface ValidationJobStartedData {
  mode: string;
  jobId: string;
  jobStatus: string;
  externalJobId: string;
  pollUrl: string;
}

export interface ValidationJobStatusData {
  jobId: string;
  jobStatus: string;
  externalJobId: string;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  score: number;
  hardstops: number;
  errors: number;
  warnings: number;
  alerts: number;
  systemErrors: number;
  ruleDetails: Array<ExternalValidationResult>;
}

export enum ValidateMismoResponseType {
  Completed = 'VALIDATION_COMPLETED',
  JobStarted = 'VALIDATION_JOB_STARTED',
  Failed = 'VALIDATION_FAILED',
}

export enum ValidationJobStatusType {
  Completed = 'VALIDATION_COMPLETED',
  Failed = 'VALIDATION_FAILED',
  TimedOut = 'VALIDATION_TIMED_OUT',
  InProgress = 'VALIDATION_IN_PROGRESS',
}

export type ValidateMismoResponse =
  | CommonApiResponse<ReviewValidationResponse, ValidateMismoResponseType.Completed>
  | CommonApiResponse<ValidationJobStartedData, ValidateMismoResponseType.JobStarted>
  | CommonApiResponse<unknown, ValidateMismoResponseType.Failed>;

export type ValidationJobStatusResponse =
  | CommonApiResponse<ValidationJobStatusData, ValidationJobStatusType.Completed | ValidationJobStatusType.InProgress>
  | CommonApiResponse<unknown, ValidationJobStatusType.Failed | ValidationJobStatusType.TimedOut>;

export interface ExternalValidationResult {
  scope: ValidationScope | null;
  page: Array<string> | null; // FE page route. Eg. ['sales-approach/adjustment' , 'subject/detail']
  path: Array<string> | null; // Field Dictionary path. Eg. ["BelowGradeFinishedArea"]
  fieldName: string | null; // Display name of the field. Eg. "Below Grade Finished Area"
  description: string; // Description of the error. Eg. "This field is required"
  status: RuleStatus; // Only 'ERROR' is implemented in the FE.
  label?: string; // Optional. For additional context, like address for different comps with same validation errors. Eg. "4309 Lawn Avenue"
  source?: string | null; // Could be from 'REVIEW' or 'VALUATION' backend.
  ruleId?: string | null; // ID used by Profet Review to identify a specific rule.
  comparableNumber?: number | null; // Profet Review's reference of the comparables' order. 0 index is for subject.
  allowOverride?: boolean; // Whether a Review rule can be overridden.
  isOverridden?: boolean; // Whether a Review rule is already overridden by the user.
  userNotes?: string | null; // Any comment added by the user while overriding the rule.
}

export interface OverrideValidationRuleRequest {
  orderId: string;
  type?: 'VALIDATION' | 'FEEDBACK';
  rules: Array<{
    ruleId: string;
    newStatus: string | null;
    userNotes: string | null;
    comparableNumber?: number;
    providerAddressed?: boolean;
  }>;
}

export interface OverrideValidationRuleResponseData {
  recalculatedScore: number;
  recalculatedRiskScore: number;
  scoreSummary: Record<string, any>;
  restrictedApprovalStatusesFound: boolean;
}

export interface Rule {
  scope: Array<ValidationScope>;
  path: Array<string>;
  fieldName: string;
  conditions: Array<ValidationCondition>;
  validation: ValidationType;

  /** Optional message from the validation config; replaces the text the frontend composes for this rule. */
  description?: string | null;
  min?: number | null;
  max?: number | null;
  minInclusive?: boolean | null;
  maxInclusive?: boolean | null;
  minListing?: number | null;
  maxListing?: number | null;
  minSold?: number | null;
  maxSold?: number | null;
  valuationType?: Array<ValuationType>;
  scenarioId?: string | null;
  requiredImages?: string[] | null;
  operator?: Operator | null;
  compareToField?: string | null;
  compareToFieldName?: string | null;
  compareToValue?: any;
  page?: Array<string>;
}

export interface ValidationRule extends Rule {
  page: Array<string>;
  ruleId: string;
  custom?: boolean;
}

/** Property-root context passed to COMPARE so it can resolve fields at the right structure/unit level. */
export type CompareContext = { property?: ExtendedPropertyDetail; structureIndex?: number; unitIndex?: number };

export interface ValidationCondition {
  scope?: ValidationScope;
  path: string;
  fieldName: string;
  comparator: Comparator;
  fieldValue: any;
  fieldDisplayValue: any;
}

export enum ValidationType {
  NotEmpty = 'NOT_EMPTY',
  Count = 'COUNT',
  AnyNotEmpty = 'ANY_NOT_EMPTY',
  AllNotEmpty = 'ALL_NOT_EMPTY',
  Match = 'MATCH',
  Range = 'RANGE',
  RequiredImages = 'REQUIRED_IMAGES',
  SelectedCompCount = 'SELECTED_COMP_COUNT',
  StrictEnumeration = 'STRICT_ENUMERATION',
  StrictNumber = 'STRICT_NUMBER',
  StrictDate = 'STRICT_DATE',
  Compare = 'COMPARE',
  RequiredImagesLabel = 'REQUIRED_IMAGES_LABEL',
}

export type ValidationScope = 'ORDER' | 'PROPERTY' | 'SUBJECT' | 'COMPARABLE' | 'SCENARIO';

/** Snapshot of an open editor's values, used to preview validation highlights live without saving. */
export interface EditorDraft {
  scope: ValidationScope;
  propertyId?: string;
  structureIndex?: number;
  unitIndex?: number;
  values: Record<string, unknown>;
}

export enum Comparator {
  equals = 'eq',
  greaterthan = 'gt',
  notequal = 'ne',
  contains = 'contains',
}

export type Operator = 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'contains';

export interface ValidatorRes {
  status: RuleStatus;
  message?: string;
}
